import { useSearchParams } from "@solidjs/router";
import { ISSUE_LIFECYCLE_STATES } from "contracts/enums/core";
import { PILLARS } from "contracts/enums/metrics";
import {
  ISSUE_SEVERITY_FILTERS,
  ISSUE_SORT_KEYS,
  ISSUE_STATUS_FILTER,
  ISSUE_STATUS_FILTERS,
  type IssueSortKey,
  type IssueStatusFilter,
} from "../../enums/issues";
import { lifecycleLabel } from "../website/issueModel";
import type { Filters } from "./filters";

// `done` is the analyst's manual close; `resolved` is the agent-verified
// close (a re-scan confirmed the defect stopped reproducing).
const STATUS_LABELS: Record<IssueStatusFilter, string> = {
  unresolved: "Unresolved",
  done: "Done",
  resolved: "Verified fixed",
  ignored: "Ignored",
  all: "All",
};
const STATUS_TABS = ISSUE_STATUS_FILTERS.map((value) => ({
  value,
  label: STATUS_LABELS[value],
}));

const LIFECYCLE_OPTIONS = [
  { value: "all", label: "All lifecycle" },
  ...ISSUE_LIFECYCLE_STATES.map((state) => ({
    value: state,
    label: lifecycleLabel(state),
  })),
];

const SORT_LABELS: Record<IssueSortKey, string> = {
  impact: "Priority",
  severity: "Severity",
  recent: "Recently updated",
};
const SORTS = ISSUE_SORT_KEYS.map((value) => ({
  value,
  label: SORT_LABELS[value],
}));

export function FilterBar(props: { filters: Filters }) {
  const [, setSearch] = useSearchParams();
  const setParam = (key: string, value: string | null) => {
    setSearch({
      [key]: value === null || value === "" ? undefined : value,
      // Changing a filter resets any open drawer so focus returns to the list.
      ...(key === "issue" ? {} : { issue: undefined }),
    });
  };

  return (
    <div class="toolbar scan-issues-toolbar">
      <fieldset class="seg" aria-label="Filter issues by status">
        {STATUS_TABS.map((tab) => (
          <button
            class={`seg-btn ${props.filters.status === tab.value ? "is-on" : ""}`}
            type="button"
            aria-pressed={props.filters.status === tab.value}
            onClick={() =>
              setParam(
                "status",
                tab.value === ISSUE_STATUS_FILTER.UNRESOLVED ? null : tab.value,
              )
            }
          >
            {tab.label}
          </button>
        ))}
      </fieldset>
      <div class="toolbar-right">
        <input
          class="search"
          type="search"
          aria-label="Search issues"
          placeholder="Search issues…"
          value={props.filters.query}
          onInput={(event) => setParam("q", event.currentTarget.value || null)}
        />
        <Select
          keyName="sev"
          label="Filter issues by severity"
          current={props.filters.severity}
          onSelect={setParam}
          options={ISSUE_SEVERITY_FILTERS.map((value) => ({
            value,
            label: value === "all" ? "All severities" : capitalize(value),
          }))}
        />
        <Select
          keyName="lc"
          label="Filter issues by lifecycle"
          current={props.filters.lifecycle}
          onSelect={setParam}
          options={LIFECYCLE_OPTIONS}
        />
        <Select
          keyName="pillar"
          label="Filter issues by scoring family"
          current={props.filters.pillar}
          onSelect={setParam}
          options={[
            { value: "all", label: "All pillars" },
            ...PILLARS.map((pillar) => ({ value: pillar, label: pillar })),
          ]}
        />
        <Select
          keyName="sort"
          label="Sort issues"
          current={props.filters.sort}
          onSelect={setParam}
          options={SORTS}
        />
      </div>
    </div>
  );
}

function Select(props: {
  keyName: string;
  label: string;
  options: Array<{ value: string; label: string }>;
  current: string;
  onSelect: (key: string, value: string | null) => void;
}) {
  return (
    <select
      class="select"
      aria-label={props.label}
      value={props.current}
      onChange={(event) => {
        const value = event.currentTarget.value;
        props.onSelect(props.keyName, value === "all" ? null : value);
      }}
    >
      {props.options.map((option) => (
        <option value={option.value}>{option.label}</option>
      ))}
    </select>
  );
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
